import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import api, { type SafetyAcceptance } from '../../services/api';

/**
 * The Scout's safety-warning acceptance log. PolarisOS shows one shared
 * warning, once per restart and at least once a day, in whichever Vortex app
 * is opened first; every "I Understand" is recorded here.
 */

interface DeviceSafetyTabProps {
  deviceId: string;
}

const PAGE_SIZE = 25;

const APP_NAMES: Record<string, string> = {
  'com.vortexmarine.vortexnav': 'VortexNav',
  'com.vortexmarine.vortexanchor': 'VortexAnchor',
  'com.vortexmarine.vortextide': 'VortexTide',
  'com.vortexmarine.truehelm': 'TrueHelm',
  'com.vortexmarine.truehelm.motor': 'TrueHelm Motor',
  'com.vortexmarine.vortexcompass': 'VortexCompass',
  'com.vortexmarine.launcher': 'PolarisOS',
};

export function appName(pkg: string | null): string {
  if (!pkg) return 'Unknown';
  return APP_NAMES[pkg] ?? pkg;
}

/** "count:86" → "#86"; a boot-time fallback id is shown as it is. */
export function restartLabel(bootId: string | null): string {
  if (!bootId) return '—';
  const m = /^count:(\d+)$/.exec(bootId);
  return m ? `#${m[1]}` : bootId;
}

function Summary({ latest, count }: { latest: SafetyAcceptance | undefined; count: number }) {
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800 p-5 flex items-start gap-4">
      <ShieldCheckIcon className={`w-8 h-8 flex-shrink-0 ${latest ? 'text-green-400' : 'text-slate-400'}`} />
      <div>
        <p className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Last accepted</p>
        {latest ? (
          <>
            <p className="mt-1 text-lg font-semibold text-white">
              {format(new Date(latest.accepted_at), 'd MMM yyyy, HH:mm')}
              <span className="ml-2 text-base font-normal text-slate-300">
                ({formatDistanceToNow(new Date(latest.accepted_at), { addSuffix: true })})
              </span>
            </p>
            <p className="mt-1 text-base text-slate-200">
              in {appName(latest.app)} · wording v{latest.version ?? '?'} · {count} acceptance{count === 1 ? '' : 's'} recorded
            </p>
          </>
        ) : (
          <p className="mt-1 text-base text-slate-200">
            Never recorded. Acceptances are logged from the PolarisOS release with the shared safety warning.
          </p>
        )}
      </div>
    </div>
  );
}

function DeviceSafetyTab({ deviceId }: DeviceSafetyTabProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['device-safety-acceptances', deviceId],
    queryFn: () => api.adminGetDeviceSafetyAcceptances(deviceId),
  });

  const rows = data?.acceptances ?? [];
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (isError) {
    return (
      <p className="rounded-lg border border-red-500/40 bg-red-950 px-4 py-3 text-base text-red-200">
        Could not load the safety acceptances.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {!isLoading && <Summary latest={rows[0]} count={rows.length} />}

      <div>
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Acceptance log</h3>
        <p className="mt-1 text-sm text-slate-300">
          Asked once per restart and at least once a day, in whichever Vortex app is opened first. Times are shown in your time zone.
        </p>
      </div>

      <div className="rounded-lg border border-slate-700 bg-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-900">
                {['Accepted', 'App', 'Wording', 'Restart', 'Received'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-sm font-semibold text-slate-300">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="border-b border-slate-700/60">
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 w-28 animate-pulse rounded bg-slate-700" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-base text-slate-300">
                    No safety acceptances recorded for this device
                  </td>
                </tr>
              ) : (
                pageRows.map((a) => (
                  <tr key={a.id} className="border-b border-slate-700/60 hover:bg-slate-700/40">
                    <td className="px-4 py-3 text-white whitespace-nowrap">
                      {format(new Date(a.accepted_at), 'd MMM yyyy HH:mm:ss')}
                    </td>
                    <td className="px-4 py-3 text-slate-100 whitespace-nowrap">{appName(a.app)}</td>
                    <td className="px-4 py-3 text-slate-100">v{a.version ?? '?'}</td>
                    <td className="px-4 py-3 text-slate-100">{restartLabel(a.boot_id)}</td>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                      {format(new Date(a.recorded_at), 'd MMM yyyy HH:mm')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {rows.length > PAGE_SIZE && (
        <div className="flex items-center justify-between pt-1">
          <p className="text-sm text-slate-300">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm text-slate-300">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeviceSafetyTab;
